import React, { useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Row, Col, Input, Button, Table } from "reactstrap";
import moment from "moment";

const STATUS_OPTIONS = [
    { key: "PAID", label: "PAID" },
    { key: "NOT_PAID", label: "NOT PAID" },
    { key: "PARTIALLY_PAID", label: "PARTIALLY PAID" },
    { key: "CANCELLED", label: "CANCELLED" },
    { key: "CHEQUE_DEPOSITED", label: "CHEQUE DEPOSITED" },
    { key: "CHEQUE_RECEIVED", label: "CHEQUE RECEIVED" },
    { key: "ECHO", label: "ECHO" },
    { key: "DUMP", label: "DUMP" },
];

const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce((acc, item) => {
    acc[item.key] = item.label;
    return acc;
}, {});

const DIMENSIONS = [
    { key: "invoice_date", label: "Invoice Date" },
    { key: "sales_person_name", label: "Sales Person" },
    { key: "status", label: "Status" },
];

const asNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const normalizeStatus = (status) => {
    const key = (status || "").trim();
    return STATUS_LABEL_MAP[key] ? key : "NOT_PAID";
};

const prettyDate = (v) => {
    if (!v) return "-";
    const m = moment(v);
    return m.isValid() ? m.format("DD-MM-YYYY") : String(v);
};

const prettyStatus = (v) => STATUS_LABEL_MAP[v] || v || "-";

const formatMoney = (n) =>
    asNumber(n).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export default function DayWiseMocReportGrid({ data = [], mocLabel = "" }) {
    const [visibleCols, setVisibleCols] = useState({
        invoice_date: true,
        sales_person_name: false,
        status: false,
    });
    const [selectedStatuses, setSelectedStatuses] = useState(
        STATUS_OPTIONS.map((s) => s.key)
    );

    const filteredRows = useMemo(() => {
        return (Array.isArray(data) ? data : []).filter((row) =>
            selectedStatuses.includes(normalizeStatus(row.status))
        );
    }, [data, selectedStatuses]);

    const dayWiseTotals = useMemo(() => {
        return filteredRows.reduce((acc, row) => {
            const dateKey = String(row.invoice_date || "");
            if (!acc[dateKey]) {
                acc[dateKey] = 0;
            }
            acc[dateKey] += asNumber(row.total_net_sales);
            return acc;
        }, {});
    }, [filteredRows]);

    const rows = useMemo(() => {
        const filtered = filteredRows;

        const grouped = filtered.reduce((acc, row) => {
            const normalized = {
                invoice_date: row.invoice_date,
                sales_person_name: row.sales_person_name || "-",
                status: normalizeStatus(row.status),
                invoice_count: asNumber(row.invoice_count),
                total_net_sales: asNumber(row.total_net_sales),
            };

            const keyParts = [];
            if (visibleCols.invoice_date) keyParts.push(String(normalized.invoice_date || ""));
            if (visibleCols.sales_person_name) keyParts.push(String(normalized.sales_person_name || ""));
            if (visibleCols.status) keyParts.push(String(normalized.status || ""));

            const groupKey = keyParts.length ? keyParts.join("||") : "ALL";

            if (!acc[groupKey]) {
                acc[groupKey] = {
                    invoice_date: visibleCols.invoice_date ? normalized.invoice_date : "ALL",
                    sales_person_name: visibleCols.sales_person_name ? normalized.sales_person_name : "ALL",
                    status: visibleCols.status ? normalized.status : "ALL",
                    invoice_count: 0,
                    total_net_sales: 0,
                };
            }

            acc[groupKey].invoice_count += normalized.invoice_count;
            acc[groupKey].total_net_sales += normalized.total_net_sales;
            return acc;
        }, {});

        return Object.values(grouped).sort((a, b) => {
            if (visibleCols.invoice_date) {
                const da = moment(a.invoice_date);
                const db = moment(b.invoice_date);
                if (da.isValid() && db.isValid() && !da.isSame(db)) {
                    return da.valueOf() - db.valueOf();
                }
            }
            if (visibleCols.sales_person_name) {
                return String(a.sales_person_name).localeCompare(String(b.sales_person_name));
            }
            return 0;
        });
    }, [filteredRows, visibleCols]);

    const totals = useMemo(() => {
        return rows.reduce(
            (acc, row) => {
                acc.invoice_count += asNumber(row.invoice_count);
                acc.total_net_sales += asNumber(row.total_net_sales);
                return acc;
            },
            { invoice_count: 0, total_net_sales: 0 }
        );
    }, [rows]);

    const shownDims = DIMENSIONS.filter((d) => visibleCols[d.key]);

    const toggleDimension = (key) => {
        setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleStatus = (key) => {
        setSelectedStatuses((prev) => {
            if (prev.includes(key)) {
                return prev.filter((x) => x !== key);
            }
            return [...prev, key];
        });
    };

    const selectAllStatuses = () => setSelectedStatuses(STATUS_OPTIONS.map((s) => s.key));
    const clearStatuses = () => setSelectedStatuses([]);

    const copyAsTabText = async () => {
        const headers = [...shownDims.map((d) => d.label), "Invoice Count", "Total Net Sales", "Day Wise Total"];
        const lines = [headers.join("\t")];

        rows.forEach((row) => {
            const cells = [];
            if (visibleCols.invoice_date) cells.push(prettyDate(row.invoice_date));
            if (visibleCols.sales_person_name) cells.push(row.sales_person_name || "-");
            if (visibleCols.status) cells.push(prettyStatus(row.status));
            cells.push(String(asNumber(row.invoice_count)));
            cells.push(formatMoney(row.total_net_sales));
            const dayTotal = row.invoice_date && row.invoice_date !== "ALL"
                ? asNumber(dayWiseTotals[String(row.invoice_date)])
                : totals.total_net_sales;
            cells.push(formatMoney(dayTotal));
            lines.push(cells.join("\t"));
        });

        lines.push(["TOTAL", String(totals.invoice_count), formatMoney(totals.total_net_sales), formatMoney(totals.total_net_sales)].join("\t"));

        try {
            await navigator.clipboard.writeText(lines.join("\n"));
            window.alert("Copied report table to clipboard");
        } catch (e) {
            window.alert("Copy failed. Please copy manually.");
        }
    };

    const reportTitle = mocLabel
        ? `Day Wise Sales Summary (${mocLabel})`
        : "Day Wise Sales Summary";

    return (
        <Card
            style={{
                marginTop: "20px",
                borderRadius: "16px",
                border: "1px solid #d8e6df",
                boxShadow: "0 14px 30px rgba(18, 86, 56, 0.08)",
                overflow: "hidden",
            }}
        >
            <CardHeader
                style={{
                    background: "linear-gradient(135deg, #0f9d73 0%, #7de2a7 100%)",
                    color: "#073b2a",
                    fontWeight: 700,
                    fontSize: "20px",
                    letterSpacing: "0.2px",
                    padding: "12px 20px",
                }}
            >
                {reportTitle}
            </CardHeader>
            <CardBody style={{ background: "#f8fcfa", padding: "18px" }}>
                <Row style={{ marginBottom: "14px" }}>
                    <Col md="6">
                        <div style={{ fontWeight: 700, marginBottom: "10px", color: "#1f3a2f" }}>Show/Hide Group Columns</div>
                        {DIMENSIONS.map((dim) => (
                            <label
                                key={dim.key}
                                style={{
                                    marginRight: "10px",
                                    marginBottom: "8px",
                                    padding: "6px 10px",
                                    border: "1px solid #dce9e2",
                                    borderRadius: "22px",
                                    background: "#ffffff",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    color: "#264133",
                                    fontWeight: 600,
                                }}
                            >
                                <Input
                                    type="checkbox"
                                    checked={visibleCols[dim.key]}
                                    onChange={() => toggleDimension(dim.key)}
                                    style={{ marginRight: "6px", marginTop: 0 }}
                                />
                                {dim.label}
                            </label>
                        ))}
                    </Col>
                    <Col md="6">
                        <div style={{ fontWeight: 700, marginBottom: "10px", color: "#1f3a2f" }}>Status Filter</div>
                        <div style={{ marginBottom: "8px" }}>
                            <Button size="sm" color="info" onClick={selectAllStatuses} style={{ marginRight: "8px" }}>
                                All
                            </Button>
                            <Button size="sm" color="secondary" onClick={clearStatuses}>
                                Clear
                            </Button>
                            <Button size="sm" color="primary" onClick={copyAsTabText} style={{ marginLeft: "8px" }}>
                                Copy
                            </Button>
                        </div>
                        {STATUS_OPTIONS.map((s) => (
                            <label
                                key={s.key}
                                style={{
                                    marginRight: "8px",
                                    marginBottom: "8px",
                                    padding: "6px 10px",
                                    border: "1px solid #dce9e2",
                                    borderRadius: "20px",
                                    background: "#ffffff",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    color: "#294136",
                                }}
                            >
                                <Input
                                    type="checkbox"
                                    checked={selectedStatuses.includes(s.key)}
                                    onChange={() => toggleStatus(s.key)}
                                    style={{ marginRight: "6px", marginTop: 0 }}
                                />
                                {s.label}
                            </label>
                        ))}
                    </Col>
                </Row>

                <div
                    className="table-responsive"
                    style={{
                        maxHeight: "420px",
                        overflowY: "auto",
                        overflowX: "auto",
                        border: "1px solid #d8e6df",
                        borderRadius: "10px",
                        background: "#fff",
                    }}
                >
                    <Table bordered striped hover size="sm" style={{ marginBottom: 0 }}>
                        <thead style={{ background: "#eef8f2", position: "sticky", top: 0, zIndex: 2 }}>
                            <tr>
                                {visibleCols.invoice_date && <th style={{ fontSize: "13px", fontWeight: 600 }}>Invoice Date</th>}
                                {visibleCols.sales_person_name && <th style={{ fontSize: "13px", fontWeight: 600 }}>Sales Person</th>}
                                {visibleCols.status && <th style={{ fontSize: "13px", fontWeight: 600 }}>Status</th>}
                                <th style={{ textAlign: "right", fontSize: "13px", fontWeight: 600 }}>Invoice Count</th>
                                <th style={{ textAlign: "right", fontSize: "13px", fontWeight: 600 }}>Total Net Sales</th>
                                <th style={{ textAlign: "right", fontSize: "13px", fontWeight: 600 }}>Day Wise Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={shownDims.length + 3} style={{ textAlign: "center" }}>
                                        No records
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row, idx) => (
                                    <tr key={`${idx}-${row.invoice_date}-${row.sales_person_name}-${row.status}`}>
                                        {visibleCols.invoice_date && <td>{row.invoice_date === "ALL" ? "ALL" : prettyDate(row.invoice_date)}</td>}
                                        {visibleCols.sales_person_name && <td>{row.sales_person_name || "-"}</td>}
                                        {visibleCols.status && <td>{row.status === "ALL" ? "ALL" : prettyStatus(row.status)}</td>}
                                        <td style={{ textAlign: "right" }}>{asNumber(row.invoice_count)}</td>
                                        <td style={{ textAlign: "right" }}>{formatMoney(row.total_net_sales)}</td>
                                        <td style={{ textAlign: "right", fontWeight: 700 }}>
                                            {formatMoney(
                                                row.invoice_date && row.invoice_date !== "ALL"
                                                    ? asNumber(dayWiseTotals[String(row.invoice_date)])
                                                    : totals.total_net_sales
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ fontWeight: 700, background: "#ecf8f1" }}>
                                <td colSpan={shownDims.length || 1}>TOTAL</td>
                                <td style={{ textAlign: "right" }}>{totals.invoice_count}</td>
                                <td style={{ textAlign: "right" }}>{formatMoney(totals.total_net_sales)}</td>
                                <td style={{ textAlign: "right" }}>{formatMoney(totals.total_net_sales)}</td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
}



// WEBPACK FOOTER //
// ./src/components/CustomComponents/DayWiseMocReportGrid.js